/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2023-2024 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2024 (c) Daniel Littfinski, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import Api, { UserClient } from 'Api/Api';
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import React, { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

interface AuthContextType {
  authed: boolean | undefined;
  user?: UserClient;
  sessionExpired: boolean;
  login: (username: string, password: string) => Promise<any>;
  logout: () => Promise<void>;
}
const AuthContext = createContext<AuthContextType>({} as AuthContextType);

// Requests that are themselves part of the session check must not trigger another check.
const isAuthRequest = (config?: InternalAxiosRequestConfig) => {
  const url = config?.url ?? '';
  return (
    /\/api\/Authentication\//i.test(url) || (config?.method?.toLowerCase() === 'get' && /\/api\/Me\/?$/i.test(url))
  );
};

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [authed, setAuthed] = useState<boolean | undefined>(undefined);
  const [user, setUser] = useState<UserClient | undefined>(undefined);
  const [sessionExpired, setSessionExpired] = useState(false);
  const authedRef = useRef(authed);
  authedRef.current = authed;

  function check_login() {
    Api.Me.self()
      .then((data) => {
        setAuthed(true);
        setUser(data.data);
      })
      .catch(() => {
        setAuthed(false);
        setUser(undefined);
      });
  }
  useEffect(() => {
    check_login();
  }, []);

  // The backend answers with 401 both for an expired session and for missing permissions.
  // Only when the session itself is gone is the user sent back to the login page.
  useEffect(() => {
    let checking = false;
    const id = axios.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401 && authedRef.current && !checking && !isAuthRequest(error.config)) {
          checking = true;
          try {
            await Api.Me.self();
          } catch {
            setSessionExpired(true);
            setAuthed(false);
            setUser(undefined);
          } finally {
            checking = false;
          }
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const contextValue = useMemo(
    () => ({
      authed,
      user,
      sessionExpired,
      login: async (username: string, password: string) => {
        await Api.Authentication.login({ Username: username, Password: password });
        await Api.Antiforgery.getAntiforgeryToken();
        const me = await Api.Me.self();
        setSessionExpired(false);
        setAuthed(true);
        setUser(me.data);
      },
      logout: async () => {
        try {
          await Api.Authentication.logout();
        } finally {
          setAuthed(false);
          setUser(undefined);
        }
      },
    }),
    [authed, user, sessionExpired],
  );

  return <AuthContext.Provider value={contextValue}> {children} </AuthContext.Provider>;
}

export const AuthConsumer = AuthContext.Consumer;

export function AuthRequired({ children }: { children: React.JSX.Element | React.JSX.Element[] }) {
  return <AuthConsumer>{(authed) => (authed.authed === false ? <></> : children)}</AuthConsumer>;
}
export function SuperRequired({ children }: { children: React.JSX.Element | React.JSX.Element[] }) {
  return <AuthConsumer>{() => children}</AuthConsumer>;
}

export function AllowedRoles({
  roles,
  children,
}: {
  roles: string[];
  children: React.JSX.Element | React.JSX.Element[];
}) {
  return (
    <AuthConsumer>
      {(authed) => (authed.user?.Roles?.some((el) => roles.includes(el)) === true ? children : <></>)}
    </AuthConsumer>
  );
}

export function NoAuthRequired({ children }: { children: React.JSX.Element | React.JSX.Element[] }) {
  return <AuthConsumer>{(authed) => (authed.authed === true ? <></> : children)}</AuthConsumer>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export function ProtectedRoute({ children }: { children: React.JSX.Element | never[] }) {
  const location = useLocation();
  return (
    <AuthConsumer>
      {(authed) =>
        authed.authed === false ? (
          <>
            <Navigate to={'/login?path=' + location.pathname} />
          </>
        ) : (
          <>{children}</>
        )
      }
    </AuthConsumer>
  );
}
